import React from 'react';
import { ArrowRight, ExternalLink } from 'lucide-react';
import { PageTransition } from '../components/layout/PageTransition';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { DecorativeShape } from '../components/DecorativeShape';
import { Animate } from '../components/ui/Animate';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
export function Writing() {
  const {
    language
  } = useLanguage();
  const isIS = language === 'is';
  const en = {
    badge: 'Against the Obvious',
    title: 'Writing',
    subtitle: 'About people and collaboration; about organisations, workplaces and companies where motivation and passion prevail; about ideas and assumptions we all seem to have bought into without questioning.',
    substackNote: 'Published on Substack. Free to read.',
    substackCta: 'Follow on Substack',
    series: [{
      slug: 'on-feedback',
      tag: 'Five articles',
      title: 'On Feedback',
      desc: 'Feedback is one of the most discussed topics in management - and one of the most misunderstood. The series challenges the dominant model and proposes something more useful.',
      status: 'Publishing now',
      statusColor: 'bg-teal/10 text-teal',
      href: '/writing/on-feedback'
    }, {
      slug: 'on-conflict',
      tag: 'Five articles',
      title: 'On Conflict',
      desc: 'Most organisations treat conflict as a problem to be managed. This series argues that the goal of minimising conflict tends to produce something worse than conflict - silence. And silence, in most organisations, is not the absence of conflict.',
      status: 'Publishing soon',
      statusColor: 'bg-amber-100 text-amber-700',
      href: '/writing/on-conflict'
    }, {
      slug: 'lessons-of-leadership',
      tag: 'Five articles',
      title: 'Lessons of Leadership',
      desc: 'The skills that made you excellent are not quite the skills that will make you effective as a leader. This series is about the transition nobody prepares you for - and what the development from there actually looks like.',
      status: 'Autumn 2026',
      statusColor: 'bg-gray-100 text-gray-500',
      href: '/writing/lessons-of-leadership'
    }, {
      slug: 'lessons-of-agility',
      tag: 'Prologue + five articles',
      title: 'The Lessons of Agility',
      desc: 'Agility has been so thoroughly colonised by frameworks and certifications that the original insight is barely visible. This series goes back to the philosophy and asks what it actually means for how we work.',
      status: 'Publishing soon',
      statusColor: 'bg-amber-100 text-amber-700',
      href: '/writing/lessons-of-agility'
    }, {
      slug: 'lessons-of-strategy',
      tag: 'Five articles',
      title: 'The Lessons of Strategy',
      desc: 'Most organisations have a mission statement where their strategy should be. This series looks at what strategy actually is, why most strategies fail the most basic tests, and what a real alternative looks like.',
      status: 'Publishing soon',
      statusColor: 'bg-amber-100 text-amber-700',
      href: '/writing/lessons-of-strategy'
    }, {
      slug: 'on-teams',
      tag: 'Four articles',
      title: 'On Teams',
      desc: 'Teams are the basic unit of how organisations get things done - yet most organisations give very little thought to how teams actually develop. The series looks at the research and what it demands of leaders.',
      status: 'Publishing soon',
      statusColor: 'bg-amber-100 text-amber-700',
      href: '/writing/on-teams'
    }]
  };
  const is = {
    badge: 'Against the Obvious',
    title: 'Skrif',
    subtitle: 'Um fólk og samstarf; um stofnanir vinnustaði og fyrirtæki þar sem áhugahvöt og eldmóður ráða ríkjum; um hugmyndir og forsendur sem við öll virðumst hafa keypt án þess að efast um.',
    substackNote: 'Birt á Substack. Ókeypis að lesa.',
    substackCta: 'Fylgjast á Substack',
    series: [{
      slug: 'on-feedback',
      tag: 'Fimm greinar',
      title: 'Um endurgjöf',
      desc: 'Endurgjöf er eitt mest rædda viðfangsefnið í stjórnun – og eitt það misskildasta. Serían skoðar ríkjandi nálganir á gagnrýninn hátt og bendir á gagnlegri leiðir.',
      status: 'Í birtingu',
      statusColor: 'bg-teal/10 text-teal',
      href: '/writing/on-feedback'
    }, {
      slug: 'on-conflict',
      tag: 'Fimm greinar',
      title: 'Um ágreininga',
      desc: 'Flestir vinnustaðir meðhöndla ágreining sem vandamál sem þarf að stjórna. Í þessari röð skoðum við hvert það leiðir - oftast framkallast eitthvað sem er verra: þögn.',
      status: 'Kemur fljótlega',
      statusColor: 'bg-amber-100 text-amber-700',
      href: '/writing/on-conflict'
    }, {
      slug: 'lessons-of-leadership',
      tag: 'Fimm greinar',
      title: 'Lærdómar af forystu',
      desc: 'Sú færni sem gerði þig framúrskarandi sem sérfræðing er ekki sú sama og gerir þig áhrifaríkan sem leiðtoga. Þessi sería fjallar um vegferðina sem enginn undirbýr þig fyrir – og hvernig þróunin þaðan lítur út í raun og veru.',
      status: 'Haust 2026',
      statusColor: 'bg-gray-100 text-gray-500',
      href: '/writing/lessons-of-leadership'
    }, {
      slug: 'lessons-of-agility',
      tag: 'Formáli + fimm greinar',
      title: 'Lærdómar frá Agile',
      desc: 'Agile hefur verið svo rækilega kaffært í utanumhaldi og vottunum að upprunalega innsýnin sést varla lengur. Í þessari seríu er horft aftur til grunnstefnunnar, raunverulegur lærdómur af Agile skoðaður og hvað það þýðir í raun fyrir vinnulagið okkar.',
      status: 'Kemur fljótlega',
      statusColor: 'bg-amber-100 text-amber-700',
      href: '/writing/lessons-of-agility'
    }, {
      slug: 'lessons-of-strategy',
      tag: 'Fimm greinar',
      title: 'Lærdómar um stefnu',
      desc: 'Flest fyrirtæki eru með stefnuyfirlýsingu þar sem stefnumótun ætti að vera. Í þessari seríu er skoðað hvað stefnumótun er í raun og veru, hvers vegna flestar áætlanir falla á einföldustu prófunum og hvernig raunveruleg stefna lítur út.',
      status: 'Kemur fljótlega',
      statusColor: 'bg-amber-100 text-amber-700',
      href: '/writing/lessons-of-strategy'
    }, {
      slug: 'on-teams',
      tag: 'Fjórar greinar',
      title: 'Um teymi',
      desc: 'Teymi eru grunneining þess hvernig stofnanir fá hlutina gerða - en samt gefa flestar stofnanir lítið til þess hvernig teymi þroskast.',
      status: 'Kemur fljótlega',
      statusColor: 'bg-amber-100 text-amber-700',
      href: '/writing/on-teams'
    }]
  };
  const c = isIS ? is : en;
  return <PageTransition data-id="element-2265" data-mp="19d03ud56dg0k">
      <div className="pt-12 pb-24" data-id="element-2266" data-mp="19bowy4gfns58">
        <section className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 mb-16" data-id="element-2267" data-mp="1ht8rluyf89rq">
          <DecorativeShape type="blob" color="bg-teal/10" className="top-0 right-0 w-[400px] h-[400px] translate-x-1/4 -translate-y-1/4 -z-10" data-id="element-2268" data-mp="1hvbsja143nb8" />
          <Animate data-id="element-2269" data-mp="17ww2ladtmk3b">
            <span className="inline-block px-4 py-2 bg-teal/10 text-teal font-medium rounded-full text-sm mb-6" data-id="element-2270" data-mp="1cu4jl7n8gi2z">
              {c.badge}
            </span>
            <h1 className="text-5xl md:text-6xl font-bold mb-6" data-id="element-2271" data-mp="20l1jtn5o1qye">{c.title}</h1>
            <p className="text-xl text-gray-500 max-w-2xl mb-8 leading-relaxed" data-id="element-2272" data-mp="25j342betvwbg">
              {c.subtitle}
            </p>
            <div className="flex flex-wrap items-center gap-4" data-id="element-2273" data-mp="1qte068fcpe7h">
              <a href="https://helgig.substack.com" target="_blank" rel="noopener noreferrer" data-id="element-2274" data-mp="1vsgr6oyhxjwg">
                <Button variant="outline" rightIcon={<ExternalLink size={16} data-id="element-2276" data-mp="2oavwo4pzjfin" />} data-id="element-2275" data-mp="2jghy5tg10s9r">
                  {c.substackCta}
                </Button>
              </a>
              <span className="text-sm text-gray-400" data-id="element-2277" data-mp="o8wapz4jba2r">{c.substackNote}</span>
            </div>
          </Animate>
        </section>
        <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2278" data-mp="eggvh9kmgzlk">
          <div className="space-y-6" data-id="element-2279" data-mp="afn7yhb3dmt7">
            {c.series.map((s, i) => <Animate key={s.slug} delay={i * 0.05} data-id="element-2280" data-mp="adk7t4g86dar" data-mpi={i}>
                <Link to={s.href} className="block group" data-id="element-2281" data-mp="1765z7uopvdqv">
                  <Card className="p-8 hover:shadow-md transition-all duration-200 border border-gray-100 hover:border-teal/30" data-id="element-2282" data-mp="173jo5l2mag3w">
                    <div className="flex flex-col sm:flex-row sm:items-start gap-4" data-id="element-2283" data-mp="y4pvg5seq9vm">
                      <div className="flex-1" data-id="element-2284" data-mp="t6emrydum5ez">
                        <div className="flex flex-wrap items-center gap-3 mb-3" data-id="element-2285" data-mp="t5mr6nk947op">
                          <span className="text-xs font-medium text-gray-400 uppercase tracking-wider" data-id="element-2286" data-mp="3iwi3fbpk84x9">
                            {s.tag}
                          </span>
                          <span className={`text-xs font-medium px-2.5 py-0.5 rounded-full ${s.statusColor}`} data-id="element-2287" data-mp="1teuvgstmuxqc">
                            {s.status}
                          </span>
                        </div>
                        <h2 className="text-xl font-bold text-charcoal mb-2 group-hover:text-teal transition-colors" data-id="element-2288" data-mp="22d6jcn347hf3">
                          {s.title}
                        </h2>
                        <p className="text-gray-500 leading-relaxed" data-id="element-2289" data-mp="2c4kr1i57znjb">
                          {s.desc}
                        </p>
                      </div>
                      <div className="flex-shrink-0 self-center" data-id="element-2290" data-mp="2m2qrdvbdolm2">
                        <div className="w-10 h-10 rounded-full bg-teal/10 text-teal flex items-center justify-center group-hover:bg-teal group-hover:text-white transition-all duration-200" data-id="element-2291" data-mp="2q4losoq6ueuk">
                          <ArrowRight size={18} data-id="element-2292" data-mp="rrc0hb6msg4q" />
                        </div>
                      </div>
                    </div>
                  </Card>
                </Link>
              </Animate>)}
          </div>
        </section>
      </div>
    </PageTransition>;
}